import { ActualLeanUser, LimitedAddress, INestedUserReference, LeanUser, LeanUserWithPhone, Note } from 'models';
import { NestedLabel, ObjectLabel } from 'models/Labels';
import { ApiTimeslot } from 'internal/substitutes/substituteModels';

interface Option {
    value: string;
}

interface Qualification {
    name: string;
}

interface Qualifications {
    id: number;
    option: Option | undefined | null;
    qualification: Qualification | undefined | null;
    value: string | null;
    calculated_at: string | null;
    expiration_date: string | null;
}

export interface Form1176Filing {
    id: number;
    business_location: number | null;
    program_id: string | null;
    status: 'REPORTED' | 'CONFIRMED' | 'NOT_FOUND';
    filed_at: string | null;
    claimed_at: string | null;
    confirmed_at: string | null;
    expires_at: string | null;
    added_at: string | null;
    last_updated: string | null;
}

export interface AdminProvider extends AdminListProvider {
    upcoming_availability?: ApiTimeslot[];
    deleted_at?: string | null;
    business_hires: number;
    qualifications: Qualifications[];
    med_flow_status: string;
    fast_fingerprints_appointments: FastFingerprintsAppointment[];
    rebook_businesses: number[];
    extra_attributes: { input: string; value: string }[];
    businesses_outlawed: number[];
    business_locations_outlawed: number[];
    last_1176_filed?: Form1176Filing | null;
    ready_to_work?: boolean;
    tandem_start_date: string | null;
    compliance_state: string;
    compliance_hold?: boolean;
    all_qualifications: string;
    first_vetted_date?: string;
    most_recent_compliance_date?: string;
    dependability_scores: DependabilityScore[];
    suspension_reasons: ISuspensionReason[];
    chatwoot_contact_id: number | null;
    customer?: number;
    tiers: AdminPerformanceTier[];
    student_id?: string;
    is_ht_user?: boolean;
    county_name?: string | null;
    sub_pool?: { id: number; name: string } | null;
    vetting_stage?: string | null;
}

export interface AdminPerformanceTier {
    id: number;
    name: string;
    order: number;
    image_url: string;
    incomplete_image_url: string;
    badges: AdminPerformanceBadge[];
    completed: boolean;
    added_at: string;
}

export interface AdminPerformanceBadge {
    id: number;
    name: string;
    description: string;
    order: number;
    image_url: string;
    incomplete_image_url: string;
    completed: boolean;
    added_at: string;
}

export interface FastFingerprintsAppointment {
    appointment_id: number;
    appointment_location_city: string;
    appointment_location_name: string;
    appointment_location_state: string;
    appointment_location_street_address: string;
    appointment_location_zip: string;
    appointment_time: string;
    id: number;
    confirmed_creation_at: string;
    confirmed_attending_at: string;
    confirmed_attended_at: string;
    received_notifications_to_confirm_attending: string;
    received_notifications_to_confirm_attendance: string;
    cancelled_at: string;
    indicated_needs_to_reschedule_at: string;
    received_reschedule_text_reminder: string;
}
export interface ThreadProvider {
    id: number;
    labels?: ObjectLabel[];
}

export interface LeanAdminProvider {
    user: AdminProviderListUser | AdminProviderListUser | LeanUserWithPhone | LeanUser;
}

export interface INestedBabysitterReference {
    id: number;
    user: INestedUserReference;
}

export interface IUserStats {
    review_average: number;
    review_count: number;
    cancellations_count: number;
    hour_reports_count: number;
    on_time_hour_reports_count: number;
    shifts_worked: number;
    streak_length: number;
}

export interface AdminListProvider {
    user: AdminProviderListUser;
    id: number;
    education: string;
    bio: string;
    birthday: string;
    pinned_notes: Note[];
    attributes: { id: number; name: string }[];
    labels: NestedLabel[];
    sub_availability: ApiTimeslot[] | null;
    cancellation_reset: string;
    outstanding_matches?: number;
    user_stats?: IUserStats;
    user_qualifications?: string;
    compliance_qualifications?: string;
    assumed_availability?: ApiTimeslot[];
    last_login: string;
    extra_attributes: { input: string; value: string }[];
    worked_in_center?: boolean;
    has_worked_in_childcare_past_6_months?: boolean;
    profile_image?: string | null;
    credential_qualifications?: CredentialQualification[];
}

export interface CredentialQualification {
    name: string;
    value: string | null;
}

export interface AdminProviderListUser {
    first_name: string;
    last_name: string;
    phone_number: string;
    id: number;
    shift_application_delay?: string | null;
    address: LimitedAddress;
    last_login: string;
    suspended_at: Date;
    email: string;
    compliance_state: string;
    show_spotlight: boolean;
    app_version: string;
    employment_start_date: string;
    ready_to_work: boolean;
    region_info: {
        area: string;
        area_id: number;
        symbol?: string | null;
    };
    sign_up_completed_at: string;
}

export const FOLLOWUP_FLAG = 'ADMIN_MAP_FOLLOWUP_FLAG';

export interface RawData {
    feature_name: string;
    raw_value: number;
}

export interface DependabilityScore {
    raw_data: RawData[];
    score: number;
    added_at: string;
}
export interface ISuspensionReason {
    reason: string;
    details: string;
    created_by: ActualLeanUser;
    added_at: string;
    unfreeze_date: string | null;
}

export interface BioInsight {
    id: number;
    user_id: number;
    overall_quality: number;
    strengths: string[];
    weaknesses: string[];
    improvement_suggestions: string[];
    marketing_summary: string;
    experience_related_to_licensed_facility: boolean;
    certifications_mentioned: string[];
    personal_traits_mentioned: string[];
    age_groups_mentioned: ('infants' | 'toddlers' | 'preschool' | 'school_age' | 'teens')[];
    professional_tone: boolean;
    grammar_and_spelling: 'good' | 'acceptable' | 'poor';
    length: 'too short' | 'appropriate' | 'too long';
}
